import { type ChangeEvent, type FC, type ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Form, type FormInstance, type FormItemProps, Input, type InputProps, type InputRef, Tooltip } from 'antd';

import { isRequiredField } from '../../../utilz';
import { routeFileSelector } from '../../../utilz/i18n';
import formLabelToolTip from '../Common/formLabelToolTip';

interface FormInputProps
  extends Omit<InputProps, 'form' | 'name'>,
    Pick<FormItemProps, 'rules' | 'colon' | 'validateTrigger' | 'name'> {
  label?: string;
  editMode?: boolean;
  forFilter?: boolean;
  margin?: string;
  singleLineMode?: boolean;
  inverMode?: boolean;
  inputType?: InputProps['type'];
  tooltip?: ReactNode;
  disableValidation?: boolean;
  useValueProp?: boolean;
  inputClassName?: string;
  form?: FormInstance;
  dataTestId?: string;
  validationRegex?: RegExp;
  mode?: string;
  isRequired?: boolean;
}

const FormInput: FC<FormInputProps> = ({
  placeholder = '',
  label = '',
  editMode = false,
  forFilter = false,
  margin = 'mb-15',
  singleLineMode = false,
  name = `testFiled-${Math.random()}`,
  rules = [],
  colon = false,
  inverMode = false,
  disabled,
  onBlur,
  className = '',
  inputType = 'text',
  onChange,
  defaultValue,
  value,
  readOnly = false,
  tooltip = false,
  suffix = false,
  style = {},
  addonAfter = null,
  autoFocus = false,
  tabIndex,
  disableValidation = false,
  useValueProp = false,
  maxLength,
  validateTrigger = false,
  inputClassName = '',
  dataTestId = '',
  validationRegex,
  form = null,
  isRequired,
  ...props
}) => {
  const isFieldRequired = ((rules.length > 0 && isRequiredField(rules) && !disableValidation) || isRequired) ?? false;

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const lastValidValue = useRef(defaultValue || '');

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validationRegex && form) {
      if (validationRegex.test(value) || value === '') {
        lastValidValue.current = value;
        form.setFieldsValue({ [name]: value });
      } else {
        form.setFieldsValue({ [name]: lastValidValue.current });
      }
    } else {
      onChange?.(e);
    }
  };

  // Tooltip
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const inputRef = useRef<InputRef>(null);
  const isValueTooltipAvailable = inputRef.current?.input?.type !== 'number';

  const handleMouseEnter = () => {
    const input = inputRef.current?.input;
    if (input) {
      const isOverflowing = input.scrollWidth > input.clientWidth;
      setShowTooltip(isOverflowing);
    }
  };

  return (
    <Tooltip title={showTooltip && isValueTooltipAvailable ? inputRef.current?.input?.value : ''}>
      <Form.Item
        className={`${inverMode ? 'invert-mode' : ''} ${margin} ${
          singleLineMode ? 'single-line-mode' : ''
        } form-input-container ${!editMode ? 'edit-mode-off' : ''} ${className}`}
        name={name}
        rules={disableValidation ? [] : rules}
        label={
          label ? (
            <div className={`${isFieldRequired ? 'fw-700' : ''}`}>
              {label}
              {isFieldRequired && <span className="required">*</span>}
            </div>
          ) : (
            false
          )
        }
        colon={colon}
        tooltip={tooltip ? formLabelToolTip(tooltip) : undefined}
        validateTrigger={validateTrigger}
      >
        {inputType === 'password' ? (
          <Input.Password
            id={name}
            disabled={disabled}
            readOnly={readOnly || !editMode}
            placeholder={editMode ? placeholder || `${t('translation:default.fields.enter')} ${label}` : ''}
            className={`${forFilter ? 'filter-input' : 'form-input'} ${inputClassName}`}
            {...(editMode && onBlur ? { onBlur } : {})}
            type={inputType}
            {...(onChange ? { onChange } : {})}
            defaultValue={defaultValue}
          />
        ) : (
          <Input
            ref={inputRef}
            onMouseEnter={handleMouseEnter}
            suffix={suffix}
            id={name}
            disabled={disabled}
            readOnly={readOnly || !editMode}
            placeholder={editMode ? placeholder || `${t('translation:default.fields.enter')} ${label}` : ''}
            className={`${forFilter ? 'filter-input' : 'form-input'} ${inputClassName}`}
            {...(editMode && onBlur ? { onBlur } : {})}
            type={inputType}
            defaultValue={defaultValue}
            style={{ ...style }}
            addonAfter={editMode ? addonAfter : null}
            autoComplete="off"
            data-testid={dataTestId}
            autoFocus={autoFocus}
            maxLength={maxLength}
            {...(tabIndex ? { tabIndex } : {})}
            {...(useValueProp ? { value } : {})}
            {...props}
            {...(onChange || validationRegex ? { onChange: handleInputChange } : {})}
          />
        )}
      </Form.Item>
    </Tooltip>
  );
};

export default FormInput;
